<script setup lang="ts">
import CheckboxToggle from "@/components/form/CheckboxToggle.vue"
import LoadingButton from "@/components/LoadingButton.vue"
import VarsityContentProcessor from "@/components/VarsityContentProcessor.vue"
import { useMediaUrl } from "@/composables"
import type { ChampionshipEventType } from "@/model/championship/championship-event-type"
import type { ChampionshipRegistrationSelectTeamTask } from "@/model/championship/championship-registration-select-team-task"
import type { TasksProps } from "@/pages/Tasks/Tasks/tasks-props"
import { useAppState } from "@/stores"
import { defineTranslations } from "@/utils/defineTranslations"
import { $currency, $date, useApp, useHttpClient, useHttpGet, useLoadingState } from "@/vf"
import { computed, ref, toRefs } from "vue"
import { type ChampionshipRegistrationAcceptEventInfoData } from "./championship-registration"
import { useRegistrationPolicy } from "./useRegistrationPolicy"

const props = defineProps<TasksProps<ChampionshipRegistrationSelectTeamTask>>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const { task } = toRefs(props)
const http = useHttpClient()
const checkboxToggled = ref(false)
const { apiLink } = useApp()
const { mediaUrl } = useMediaUrl()

/*─────────────────────────────────────┐
│  fetching                            │
└─────────────────────────────────────*/
const { data } = await useHttpGet<ChampionshipRegistrationAcceptEventInfoData>(
    `/championship/registration/${task.value.organizationId}/accept-event-info/${task.value.id}`,
)

const championship = computed(() => data.value!.championship!)
const policy = useRegistrationPolicy(
    computed(() => data.value?.permissions),
    computed(() => props.viewMode),
)

/*─────────────────────────────────────┐
│  accept                              │
└─────────────────────────────────────*/
const submitting = useLoadingState()

function submitAccept() {
    submitting.while(async () => {
        await http.post(
            `/championship/registration/${task.value.organizationId}/accept-event-info/${task.value.id}?viewMode=${props.viewMode}`,
        )
        emit("updated")
    })
}

const trans = defineTranslations({
    checkbox_text: {
        de: "Ich habe die Informationen gelesen und akzeptiere die Teilnahmebedingungen.",
        en: "I have read the information and accept the terms of participation.",
    },
    accept: {
        de: "Akzeptieren",
        en: "Accept",
    },
    registration_date: {
        de: "bis {date}",
        en: "until {date}",
    },
    hint_waiting_list: {
        de: "Du bewirbst dich erstmal nur für einen Wartelistenplatz!",
        en: "You only apply for a waiting list place!",
    },
    downloads: {
        de: "Downloads",
        en: "Downloads",
    },
})

/*─────────────────────────────────────┐
│  Dev Buttons                         │
└─────────────────────────────────────*/
const appState = useAppState()
async function resetRegistration() {
    await http.post(`/championship/registration/${data.value!.registrationId}/dev-reset`)
    emit("updated")
}

async function deleteRegistration() {
    await http.post(`/championship/registration/${data.value!.registrationId}/dev-delete`)
    emit("updated")
}

async function deleteOrder() {
    await http.post(`/championship/registration/${data.value!.registrationId}/reset-order`)
    emit("updated")
}
</script>
<template>
    <div class="event-card-show overflow-hidden mb-4" style="max-width: 100%">
        <div class="event-card-show__inner">
            <div class="row">
                <div class="col-xl-3 col-lg-6 col-12 order-1">
                    <h3 class="event-card-show__title">{{ $t("@championship:championship.eventInformation") }}</h3>
                    <p class="mb-0">
                        <b>{{ $t("@championship:championship.eventType") }}:</b>
                        {{
                            (championship.eventTypes as ChampionshipEventType[] | undefined)
                                ?.map(c => c.name)
                                .join(", ")
                        }}
                    </p>
                    <p>
                        <b>{{ $t("@championship:championship.bids") }}:</b>
                        {{ championship.bids?.map(c => c.name).join(", ") }}
                    </p>
                    <address>
                        {{ championship.brandName }}
                        <br />
                        {{ championship.brandStreet }} {{ championship.brandStreetNumber }}
                        <br />
                        {{ championship.brandZip }} {{ championship.brandCity }}
                    </address>
                    <b>{{ $t("@championship:championship.location") }}:</b>
                    <address>
                        {{ championship.location }}
                        <br />
                        {{ championship.locationStreet }} {{ championship.locationStreetNumber }}
                        <br />
                        {{ championship.locationZip }} {{ championship.locationCity }}
                    </address>
                </div>
                <div class="col-xl-9 col-12 order-3 order-xl-2 d-none d-lg-block">
                    <div class="row">
                        <div class="col-lg-9">
                            <div class="event-card-show__registration d-none d-lg-block mb-4">
                                <h3 class="event-card-show__title mb-2">
                                    {{ $t("@championship:championship.registration") }}
                                </h3>
                                <time :datetime="data?.price.registrationUntil || championship.registrationUntil">
                                    {{
                                        trans("registration_date", {
                                            date: $date(
                                                data?.price.registrationUntil || championship.registrationUntil,
                                            ),
                                        })
                                    }}
                                </time>
                            </div>

                            <div v-if="championship.infoDocumentClientName" class="mb-4">
                                <h3 class="event-card-show__title mb-2">{{ trans("downloads") }}</h3>
                                <div>
                                    <a
                                        :href="
                                            apiLink(
                                                `/championship/registration/${task.organizationId}/download-event-info/${task.id}`,
                                            )
                                        "
                                    >
                                        <i class="fas fa-file-download mr-1"></i>
                                        {{ championship.infoDocumentClientName }}
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div class="col-lg-3">
                            <div v-if="championship.logoName">
                                <img
                                    :src="mediaUrl(championship.logoName)"
                                    :alt="championship.logoName"
                                    class="img-fluid mb-3"
                                />
                            </div>
                        </div>
                    </div>

                    <h3 class="event-card-show__title">{{ $t("@championship:championship.price_information") }}</h3>
                    <table class="table event-card-show__table">
                        <tbody>
                            <tr>
                                <th></th>
                                <th class="text-right">{{ $t("@championship:championship.prices.early_rate") }}</th>
                                <th class="text-right">
                                    {{ $t("@championship:championship.prices.normal_rate") }}
                                </th>
                            </tr>
                            <tr>
                                <td>{{ $t("@championship:championship.prices.active_participant") }}</td>
                                <td class="text-right">{{ $currency(data?.price.priceEarlyActiveCents) }}</td>
                                <td class="text-right">{{ $currency(data?.price.priceNormalActiveCents) }}</td>
                            </tr>
                            <tr>
                                <td>{{ $t("@championship:championship.prices.plus_category") }}</td>
                                <td class="text-right">
                                    {{ $currency(data?.price.priceEarlyAdditionalCategoryCents) }}
                                </td>
                                <td class="text-right">
                                    {{ $currency(data?.price.priceNormalAdditionalCategoryCents) }}
                                </td>
                            </tr>
                            <tr>
                                <td>{{ $t("@championship:championship.prices.coach") }}</td>
                                <td class="text-right">{{ $currency(data?.price.priceEarlyCoachCents) }}</td>
                                <td class="text-right">{{ $currency(data?.price.priceNormalCoachCents) }}</td>
                            </tr>
                            <tr>
                                <td>{{ $t("@championship:championship.prices.escort") }}</td>
                                <td class="text-right">{{ $currency(data?.price.priceEarlyEscortCents) }}</td>
                                <td class="text-right">{{ $currency(data?.price.priceNormalEscortCents) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="row border-top border-light pt-3" v-if="championship.infoText">
                <div class="col-12">
                    <VarsityContentProcessor :content="championship.infoText" />
                </div>
            </div>
        </div>

        <!-- <div style="border: 3px solid #f0f; padding: 1rem"> -->
        <!--     <h2 style="color: #f0f">Dev</h2> -->
        <!--     <Suspense> -->
        <!--         <ChampionshipRegistrationFinalDocuments -->
        <!--             v-bind="{...props, task:issue.tasks!.find(t => t.taskType === -->
        <!--     'ChampionshipRegistrationFinalDocuments')! as ChampionshipRegistrationFinalDocumentsTask}" -->
        <!--         /> -->
        <!--     </Suspense> -->
        <!-- </div> -->

        <div class="px-5 pt-3" v-if="props.task.state === 'open'">
            <div class="alert alert-danger mb-4" v-if="championship.waitingListEnabled">
                {{ trans("hint_waiting_list") }}
            </div>

            <CheckboxToggle v-model="checkboxToggled" class="mb-4">
                <VarsityContentProcessor :content="trans('checkbox_text')" />
            </CheckboxToggle>

            <div class="d-flex justify-content-end">
                <LoadingButton
                    :loading="submitting"
                    :disabled="!checkboxToggled || !policy.AcceptEventInfo"
                    @click="submitAccept"
                >
                    {{ trans("accept") }}
                </LoadingButton>
            </div>
        </div>
    </div>

    <div class="p-5" v-if="appState.devMode">
        <button type="button" class="btn btn-primary" @click="resetRegistration()">DEV: Zurücksetzen</button>
        <button type="button" class="btn btn-primary" @click="deleteRegistration()">DEV: Vorgang Löschen</button>
        <button type="button" class="btn btn-primary" @click="deleteOrder()">DEV: Order löschen</button>
    </div>
</template>
